import React from "react";
import { useNavigate } from "react-router-dom";

import { Cog } from "../../../components/icons/svg/outline";
import {
  getProjectCustomerLabel,
  getProjectTitle,
} from "../../../utils/business";
import { getUserDisplayName } from "../../../utils/business/user";
import {
  emptyRfiCardSummary,
  emptyTmCardSummary,
} from "../shared/projectTicketSummaries";
import {
  ProjectRfiSummaryTile,
  ProjectTmSummaryTile,
} from "./CompactTicketSummary";
import {
  getProjectStatusConfig,
  PROJECT_HOME_CHIP_CLASS,
  PROJECT_HOME_MANAGER_CHIP_CLASS,
  PROJECT_HOME_STATUS_ON_PRIMARY_CLASS,
} from "./projectHomeHelpers";

/**
 * Project summary card for the Projects landing page.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.project - Project API row.
 * @param {(project: Object) => void} [props.onEdit] - Opens project edit modal.
 * @param {Object} [props.tmSummary] - Compact T&M status summary for this project.
 * @param {Object} [props.rfiSummary] - Compact RFI status summary for this project.
 * @param {boolean} [props.ticketsLoading=false] - Whether ticket lists are still loading.
 * @returns {JSX.Element}
 */
const ProjectCard = ({
  project,
  onEdit,
  tmSummary = emptyTmCardSummary(),
  rfiSummary = emptyRfiCardSummary(),
  ticketsLoading = false,
}) => {
  const navigate = useNavigate();
  const status = getProjectStatusConfig(Boolean(project.enabled));
  const StatusIcon = status.Icon;
  const projectNumber =
    project.number || project.project_number || `Project ${project.id}`;
  const projectTitle = getProjectTitle(project);
  const managers = project.managers || [];
  const projectPath = `/projects/${project.id}`;

  // Number + name share size/leading so the title row reads as one line.
  const titleLineClassName = "text-lg leading-7 tracking-tight";
  // Card chips are one step larger than the shared list-view chip chrome.
  const cardChipClassName = `${PROJECT_HOME_CHIP_CLASS} h-5 text-xs`;

  /**
   * Open the project page when the card is activated.
   *
   * @returns {void}
   */
  const openProject = () => {
    navigate(projectPath);
  };

  return (
    <article
      role="link"
      tabIndex={0}
      aria-label={`View ${projectTitle}`}
      className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm shadow-primary-900/5 transition duration-200 hover:-translate-y-0.5 hover:border-secondary-200 hover:shadow-xl hover:shadow-primary-900/10 focus:outline-none focus:ring-2 focus:ring-secondary-100"
      onClick={openProject}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openProject();
        }
      }}
    >
      <div className="flex flex-col overflow-hidden">
        <div className="flex min-h-7 items-center justify-between gap-2 bg-primary px-3.5 py-1.5">
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            <span
              className={`flex-shrink-0 font-semibold tabular-nums text-white ${titleLineClassName}`}
            >
              {projectNumber}
            </span>
            <span
              className={`min-w-0 truncate font-normal text-white ${titleLineClassName}`}
            >
              {projectTitle}
            </span>
          </div>

          <div className="flex flex-shrink-0 items-center gap-1">
            {onEdit ? (
              <button
                type="button"
                className="inline-flex h-6 w-6 items-center justify-center rounded text-primary-100 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30"
                aria-label={`Edit ${projectTitle}`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onEdit(project);
                }}
              >
                <Cog className="h-4 w-4" aria-hidden />
              </button>
            ) : null}
            <span
              className={`${cardChipClassName} ${
                project.enabled
                  ? PROJECT_HOME_STATUS_ON_PRIMARY_CLASS
                  : status.className
              }`}
            >
              <StatusIcon className="h-3 w-3" />
              {status.label}
            </span>
          </div>
        </div>

        <div className="flex min-h-6 items-center justify-between gap-2 bg-gray-200 px-3.5 py-1">
          <div className="min-w-0 flex-1 truncate text-sm font-normal leading-6 text-gray-700">
            {getProjectCustomerLabel(project)}
          </div>
          <div className="flex h-6 flex-shrink-0 items-center justify-end gap-1">
            {managers.length > 0 ? (
              managers.map((manager) => {
                const name = getUserDisplayName(manager, "");
                if (!name) return null;
                return (
                  <span
                    key={manager.id || name}
                    title={name}
                    className={`${cardChipClassName} max-w-[7.5rem] truncate whitespace-nowrap ${PROJECT_HOME_MANAGER_CHIP_CLASS}`}
                  >
                    {name}
                  </span>
                );
              })
            ) : (
              <span
                className={`${cardChipClassName} whitespace-nowrap border-gray-300 bg-gray-100 text-gray-500`}
              >
                No manager
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-1 flex flex-1 flex-col gap-1 px-3.5 pb-2.5">
        <div className="grid grid-cols-2 gap-1.5">
          <ProjectTmSummaryTile
            summary={tmSummary}
            isLoading={ticketsLoading}
          />
          <ProjectRfiSummaryTile
            summary={rfiSummary}
            isLoading={ticketsLoading}
          />
        </div>
      </div>
    </article>
  );
};

export default ProjectCard;
